import { useTranslation } from 'react-i18next';
import { DatePicker, Flex, InputNumber, Select, Typography, Switch, Form } from 'antd';
import { Frequency } from 'rrule';
import { useMachineDetailsRobotSchedule } from '../../../../context/MachineDetailsRobotScheduleContext';
import { useScheduleUpsertModalB50 } from '../../../../context/ScheduleCreateModalContext';
import {
  FREQUENCY_OPTIONS,
  FREQUENCY_OPTIONS_LABELS,
  FREQUENCY_OPTIONS_PLURAL_LABELS,
  WEEKDAYS_OPTIONS,
  WEEKDAY_LABEL_KEYS,
} from '../../../../types/MachineDetailsRobotSchedule.types';
import {
  EveryText,
  FrequencyText,
  OptionalText,
  RepeatingTaskText,
  RepeatingTaskFormItem,
  ScheduleEndDateText,
} from './RepeatingTask.styles';
import { kccThemeConstants } from 'config/theme/kccTheme/kccTheme';
import { KccCheckableTag } from 'lib/components/KccCheckableTag/KccCheckableTag';
import { SvgIcon } from 'lib/components/SvgIcon/SvgIcon';
import { DateTime } from 'lib/utils/date-handling/DateTime';
import { DATE_FORMAT_PATTERN } from 'lib/utils/date-handling/DateTime.types';

export const RepeatingTaskB50 = (): JSX.Element => {
  const { t, i18n } = useTranslation();

  const { isReadOnly } = useMachineDetailsRobotSchedule();
  const { form, isRepeating, handleRepeatingChange, handleWeekdaysChange } = useScheduleUpsertModalB50();

  const scheduleDate = Form.useWatch('scheduleDate', form);
  const scheduleFrequency = Form.useWatch('scheduleFrequency', form);
  const scheduleEvery = Form.useWatch('scheduleEvery', form);
  const weekdays = Form.useWatch('weekdays', form);
  const scheduleEndDate = Form.useWatch('scheduleEndDate', form);

  return (
    <Flex vertical gap={kccThemeConstants.SPACING.LG} style={{ marginLeft: 36 }}>
      <Flex align="center" gap={kccThemeConstants.SPACING.XS}>
        <Switch checked={isRepeating} disabled={isReadOnly} onChange={handleRepeatingChange} />
        <RepeatingTaskText>{t('createSchedule.repeatingTask')}</RepeatingTaskText>
      </Flex>

      {isRepeating && (
        <>
          <Flex gap={kccThemeConstants.SPACING.SM}>
            <div style={{ flex: 1 }}>
              <FrequencyText>{t('createSchedule.frequency')}</FrequencyText>

              <RepeatingTaskFormItem
                name="scheduleFrequency"
                rules={[{ required: true, message: t('createSchedule.frequencyRequired') }]}
              >
                <Select
                  style={{ width: '100%', height: '40px', marginTop: kccThemeConstants.SPACING.XS }}
                  className="schedule-frequency-active-border"
                  options={FREQUENCY_OPTIONS.map(option => ({
                    label: t(option.label).toLowerCase(),
                    value: option.value,
                  }))}
                  onChange={(value): void => {
                    if (value !== Frequency.WEEKLY) {
                      form.setFieldsValue({ weekdays: [] });
                    }
                  }}
                  suffixIcon={<SvgIcon name="chevronDown" />}
                />
              </RepeatingTaskFormItem>
            </div>

            <div style={{ flex: 1 }}>
              <EveryText>{t('createSchedule.every')}</EveryText>

              <RepeatingTaskFormItem
                name="scheduleEvery"
                rules={[
                  {
                    required: true,
                    message: t('createSchedule.everyRequired', {
                      frequency: t(FREQUENCY_OPTIONS_PLURAL_LABELS[scheduleFrequency ?? Frequency.WEEKLY]),
                    }),
                  },
                ]}
              >
                <InputNumber
                  inputMode="numeric"
                  min={1}
                  max={5}
                  precision={0}
                  step={1}
                  value={scheduleEvery}
                  style={{ width: '100%', height: '40px', marginTop: kccThemeConstants.SPACING.XS }}
                  suffix={
                    <Typography.Text style={{ lineHeight: '20px' }}>
                      {t(FREQUENCY_OPTIONS_LABELS[scheduleFrequency ?? Frequency.WEEKLY])}
                    </Typography.Text>
                  }
                />
              </RepeatingTaskFormItem>
            </div>
          </Flex>

          {scheduleFrequency === Frequency.WEEKLY && (
            <RepeatingTaskFormItem
              name="weekdays"
              className="repeating-task-weekdays"
              rules={[{ required: true, message: t('createSchedule.daysOfWeekRequired') }]}
            >
              <Flex gap={kccThemeConstants.SPACING.XS} wrap>
                {WEEKDAYS_OPTIONS.map(tag => (
                  <KccCheckableTag
                    key={tag}
                    checked={weekdays?.includes(tag) ?? false}
                    disabled={isReadOnly}
                    onChange={(checked): void => handleWeekdaysChange(checked, tag)}
                    style={{ textTransform: 'capitalize' }}
                  >
                    {t(WEEKDAY_LABEL_KEYS[tag])}
                  </KccCheckableTag>
                ))}
              </Flex>
            </RepeatingTaskFormItem>
          )}

          <Flex gap={kccThemeConstants.SPACING.SM}>
            <div style={{ flex: 1 }}>
              <Flex vertical gap={kccThemeConstants.SPACING.XS}>
                <Flex align="center" gap={kccThemeConstants.SPACING.XXS}>
                  <ScheduleEndDateText>{t('createSchedule.scheduleEndDate')}</ScheduleEndDateText>
                  <OptionalText>{t('createSchedule.optional')}</OptionalText>
                </Flex>

                <RepeatingTaskFormItem name="scheduleEndDate">
                  <DatePicker
                    popupClassName="kcc-theme-date-picker-popup"
                    className={scheduleEndDate ? 'schedule-picker-active-border' : ''}
                    style={{
                      width: '100%',
                      height: '40px',
                    }}
                    disabledDate={(current): boolean =>
                      Boolean(current && scheduleDate && current.isBefore(scheduleDate, 'day'))
                    }
                    format={(date): string =>
                      DateTime.formatDateByLocale(i18n.language, date.toISOString(), DATE_FORMAT_PATTERN.DATE)
                    }
                  />
                </RepeatingTaskFormItem>
              </Flex>
            </div>

            <div style={{ flex: 1 }} />
          </Flex>
        </>
      )}
    </Flex>
  );
};
